import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useUsers, useDeleteUser } from '../hooks/useUsers';
import { usePermission } from '../hooks/usePermission';
import { useConfirm } from '../context/ConfirmDialogContext';
import { useSnackbar } from '../context/SnackbarContext';
import { CreateUserDialog } from '../components/users/CreateUserDialog';
import { SshKeysSection } from '../components/users/SshKeysSection';
import type { UserEntry } from '../api/users';

export function Users() {
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState<UserEntry | null>(null);
  const { data: users, isLoading, error } = useUsers();
  const deleteMutation = useDeleteUser();
  const canManage = usePermission('users.manage');
  const confirm = useConfirm();
  const showSnackbar = useSnackbar();

  async function handleDelete(username: string, e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirm(
      `This will permanently delete the Linux user account and home directory.`,
      { title: `Delete user "${username}"?`, confirmPhrase: `DELETE ${username}`, danger: true },
    );
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(username);
      showSnackbar(`User "${username}" deleted`, 'success');
      if (drawerUser?.username === username) setDrawerUser(null);
    } catch {
      showSnackbar('Failed to delete user', 'error');
    }
  }

  const columns: GridColDef<UserEntry>[] = [
    { field: 'username', headerName: 'Username', width: 160, renderCell: (p: GridRenderCellParams<UserEntry>) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value as string}</Typography>
    )},
    { field: 'name', headerName: 'Full Name', flex: 1, valueGetter: (_v, row) => row.name ?? '—' },
    { field: 'email', headerName: 'Email', flex: 1, valueGetter: (_v, row) => row.email ?? '—' },
    { field: 'created_at', headerName: 'Created', width: 180,
      valueGetter: (_v, row) => row.created_at ? new Date(row.created_at).toLocaleString() : '—' },
    { field: 'managed', headerName: 'Type', width: 110, renderCell: (p: GridRenderCellParams<UserEntry>) => (
      <Chip
        label={p.value ? 'Managed' : 'System'}
        size="small"
        color={p.value ? 'primary' : 'default'}
      />
    )},
    ...(canManage ? [{
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p: GridRenderCellParams<UserEntry>) =>
        p.row.managed ? (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => handleDelete(p.row.username, e)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        ) : null,
    } as GridColDef<UserEntry>] : []),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Users</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        )}
      </Box>

      {isLoading && <CircularProgress />}
      {error && <Typography color="error">Failed to load users</Typography>}
      {users && (
        <DataGrid
          rows={users}
          columns={columns}
          getRowId={(r) => r.username}
          density="compact"
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ bgcolor: 'background.paper', cursor: 'pointer' }}
          onRowClick={(params) => setDrawerUser(params.row as UserEntry)}
        />
      )}

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <Drawer
        anchor="right"
        open={!!drawerUser}
        onClose={() => setDrawerUser(null)}
        PaperProps={{ sx: { width: 420, p: 3 } }}
      >
        {drawerUser && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{drawerUser.username}</Typography>
              <IconButton onClick={() => setDrawerUser(null)}><CloseIcon /></IconButton>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>Full Name:</strong> {drawerUser.name ?? '—'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {drawerUser.email ?? '—'}</Typography>
              <Typography variant="body2"><strong>UID:</strong> {drawerUser.uid}</Typography>
              <Typography variant="body2">
                <strong>Type:</strong>{' '}
                <Chip
                  label={drawerUser.managed ? 'Managed' : 'System'}
                  size="small"
                  color={drawerUser.managed ? 'primary' : 'default'}
                  sx={{ ml: 0.5 }}
                />
              </Typography>
              {drawerUser.created_at && (
                <Typography variant="body2">
                  <strong>Created:</strong> {new Date(drawerUser.created_at).toLocaleString()}
                </Typography>
              )}
            </Stack>

            <Divider />

            {drawerUser.managed ? (
              <SshKeysSection username={drawerUser.username} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                System user — SSH key management not available.
              </Typography>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {canManage && drawerUser.managed && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={(e) => handleDelete(drawerUser.username, e)}
                disabled={deleteMutation.isPending}
                fullWidth
              >
                Delete User
              </Button>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
